import React from "react";
import { FileText, MessageCircle, Mic, Sparkles } from "lucide-react";
import howItWorksImage from "@/assets/images/fieldcoach-how-it-works.jpg";
import HowItWorksStep from "../HowItWorksStep/HowItWorksStep";

const STEPS = [
  {
    icon: Mic,
    title: "Capture the Conversation",
    description:
      "Reps record every customer appointment from the FieldCoach mobile app — built into their existing CRM workflow with a single tap.",
    tagline: "Every call. Every rep. Every week.",
  },
  {
    icon: FileText,
    title: "Transcribe with Voice ID",
    description:
      "Calls are transcribed automatically with speaker separation, so every word — rep and customer — is captured and attributed correctly.",
    tagline: "Word-for-word. Crystal-clear attribution.",
  },
  {
    icon: Sparkles,
    title: "AI Analyzes Every Detail",
    description:
      "FieldCoach scores script adherence, evaluates objection handling, and tracks tone and sentiment — surfacing patterns no manager could catch alone.",
    tagline: "Behavior becomes visible. Drift becomes measurable.",
  },
  {
    icon: MessageCircle,
    title: "Coach from Evidence",
    description:
      "Managers review timestamped insights, leave coaching comments, and prioritize the conversations that will move the needle most in the next 1:1.",
    tagline: "Coach what happened. Reinforce what works.",
  },
];

const HowItWorksBlock: React.FC = () => (
  <section
    className="relative bg-mist py-24 dark:bg-slate-900"
    id="how-it-works"
  >
    <div className="mx-auto max-w-7xl px-6">
      <div className="mx-auto mb-14 max-w-6xl">
        <div className="grid min-h-[270px] grid-cols-1 items-stretch overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0226] via-secondary to-[#2A1A66] shadow-2xl md:grid-cols-[1.05fr_1fr]">
          <div className="relative aspect-video overflow-hidden md:aspect-auto">
            <img
              alt="Home service manager reviewing FieldCoach on a tablet"
              className="block h-full w-full object-cover object-[50%_30%]"
              loading="lazy"
              src={howItWorksImage}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-secondary via-secondary/50 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-secondary"
            />
          </div>

          <div className="relative z-10 flex flex-col justify-center px-8 py-10 md:px-12">
            <h2 className="text-4xl font-extrabold leading-none tracking-tight text-gold md:text-5xl">
              How It Works
            </h2>
            <p className="mt-3.5 max-w-md text-base leading-relaxed text-white/85">
              A disciplined system that turns every customer conversation into
              measurable performance.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, index) => (
          <HowItWorksStep
            description={step.description}
            icon={step.icon}
            key={step.title}
            showConnector={index > 0}
            tagline={step.tagline}
            title={step.title}
          />
        ))}
      </div>
    </div>
  </section>
);

export default HowItWorksBlock;
